import { BatchJobType } from "@sonder/ui/source/styled/components/ToastMessage";
import {
  FilesAttachable,
  TaskContextSubjectTypeEnum,
  TaskFilterInput,
  TaskSortInput,
  TypeEnum,
} from "typings/globalGQLTypes";

// Exports
export enum ActionType {
  SetFilters = "SET_FILTERS",
  UnsetFilters = "UNSET_FILTERS",
  BulkSetFilters = "BULK_SET_FILTERS",
  SetSortOptions = "SET_SORT_OPTIONS",
  SetGroupByOptions = "SET_GROUP_BY_OPTIONS",
  AddUndo = "ADD_UNDO",
  RemoveUndo = "REMOVE_UNDO",
  ExpandGroup = "EXPAND_GROUP",
  CollapseGroup = "COLLAPSE_GROUP",
  SelectTasksAction = "SELECT_TASKS",
  DeselectTasksAction = "DESELECT_TASKS",
  ToggleTaskAction = "TOGGLE_TASK",
  AddBatchJobAction = "ADD_BATCH_JOB",
  RemoveBatchJobAction = "REMOVE_BATCH_JOB",
  AddRefetchListenerAction = "ADD_REFETCH_LISTENER",
  RemoveRefetchListenerAction = "REMOVE_REFETCH_LISTENER",
  RefetchQueriesAction = "REFETCH_QUERIES",
  ClearRefetchQueriesAction = "CLEAR_REFETCH_QUERIES",
  AddValidationException = "ADD_VALIDATION_EXCEPTION",
  ClearValidationException = "CLEAR_VALIDATION_EXCEPTION",
  AddFileUploadAttachments = "ADD_FILE_UPLOAD_ATTACHMENTS",
  ClearFileUploadAttachments = "CLEAR_FILE_UPLOAD_ATTACHMENTS",
  SetCreateTaskModalState = "SET_CREATE_TASK_MODAL_STATE",
}
export enum TaskUndoTypes {
  CompleteTask = "COMPLETE_TASK",
}

export interface SetFiltersAction {
  type: ActionType.SetFilters;
  payload: TaskFilterInput;
}
export const setFilters = (filter: TaskFilterInput): SetFiltersAction => ({
  type: ActionType.SetFilters,
  payload: filter,
});

export interface UnsetFiltersAction {
  type: ActionType.UnsetFilters;
  payload: TaskFilterInput;
}
export const unsetFilters = (filter: TaskFilterInput): UnsetFiltersAction => ({
  type: ActionType.UnsetFilters,
  payload: filter,
});

export interface BulkSetFiltersAction {
  type: ActionType.BulkSetFilters;
  payload: TaskFilterInput;
}
export const bulkSetFilters = (
  filter: TaskFilterInput,
): BulkSetFiltersAction => ({
  type: ActionType.BulkSetFilters,
  payload: filter,
});

export interface SetSortOptionsAction {
  type: ActionType.SetSortOptions;
  payload: TaskSortInput[];
}
export const setSortOptions = (
  sort: TaskSortInput[],
): SetSortOptionsAction => ({
  type: ActionType.SetSortOptions,
  payload: sort,
});

export interface SetGroupByOptionsAction {
  type: ActionType.SetGroupByOptions;
  payload: GroupBy;
}
export const setGroupByOptions = (
  groupBy: GroupBy,
): SetGroupByOptionsAction => ({
  type: ActionType.SetGroupByOptions,
  payload: groupBy,
});

export interface AddUndoAction {
  type: ActionType.AddUndo;
  payload: {
    taskId: string;
    message: string;
  };
}
export const addUndo = (taskId: string, message: string): AddUndoAction => ({
  type: ActionType.AddUndo,
  payload: { taskId, message },
});

export interface RemoveUndoAction {
  type: ActionType.RemoveUndo;
  payload: {
    taskId: string;
  };
}
export const removeUndo = (taskId: string): RemoveUndoAction => ({
  type: ActionType.RemoveUndo,
  payload: { taskId },
});

export interface CollapseGroupAction {
  type: ActionType.CollapseGroup;
  payload: {
    groupId: CollapsedGroupId;
  };
}
export const collapseGroup = (
  groupId: CollapsedGroupId,
): CollapseGroupAction => ({
  type: ActionType.CollapseGroup,
  payload: { groupId },
});

export interface ExpandGroupAction {
  type: ActionType.ExpandGroup;
  payload: {
    groupId: CollapsedGroupId;
  };
}
export const expandGroup = (groupId: CollapsedGroupId): ExpandGroupAction => ({
  type: ActionType.ExpandGroup,
  payload: { groupId },
});

export interface SelectTasksAction {
  type: ActionType.SelectTasksAction;
  payload: {
    taskIds: string[];
  };
}
export const selectTasks = (taskIds: string[]): SelectTasksAction => ({
  type: ActionType.SelectTasksAction,
  payload: {
    taskIds,
  },
});

export interface DeselectTasksAction {
  type: ActionType.DeselectTasksAction;
  payload: {
    taskIds: string[];
  };
}
export const deselectTasks = (taskIds: string[]): DeselectTasksAction => ({
  type: ActionType.DeselectTasksAction,
  payload: {
    taskIds,
  },
});

export interface ToggleTaskAction {
  type: ActionType.ToggleTaskAction;
  payload: {
    taskId: string;
  };
}
export const toggleTask = (taskId: string): ToggleTaskAction => ({
  type: ActionType.ToggleTaskAction,
  payload: {
    taskId,
  },
});

export interface AddBatchJobAction {
  type: ActionType.AddBatchJobAction;
  payload: {
    jobId: string;
    jobType: BatchJobType;
  };
}
export const addBatchJob = (
  jobId: string,
  jobType: BatchJobType,
): AddBatchJobAction => ({
  type: ActionType.AddBatchJobAction,
  payload: {
    jobId,
    jobType,
  },
});

export interface RemoveBatchJobAction {
  type: ActionType.RemoveBatchJobAction;
  payload: {
    jobId: string;
  };
}
export const removeBatchJob = (jobId: string): RemoveBatchJobAction => ({
  type: ActionType.RemoveBatchJobAction,
  payload: {
    jobId,
  },
});

export interface AddRefetchListenerAction {
  type: ActionType.AddRefetchListenerAction;
  payload: {
    queryNames: QueryNameType[];
    uuid: string;
  };
}

export const addRefetchListener = (
  queryNames: QueryNameType[],
  uuid: string,
): AddRefetchListenerAction => ({
  type: ActionType.AddRefetchListenerAction,
  payload: {
    queryNames,
    uuid,
  },
});

export interface RemoveRefetchListenerAction {
  type: ActionType.RemoveRefetchListenerAction;
  payload: {
    uuid: string;
  };
}

export const removeRefetchListener = (
  uuid: string,
): RemoveRefetchListenerAction => ({
  type: ActionType.RemoveRefetchListenerAction,
  payload: {
    uuid,
  },
});

export interface RefetchQueriesAction {
  type: ActionType.RefetchQueriesAction;
  payload: {
    queryNames: QueryNameType[];
  };
}

export const refetchQueries = (
  queryNames: QueryNameType[],
): RefetchQueriesAction => ({
  type: ActionType.RefetchQueriesAction,
  payload: {
    queryNames,
  },
});

export interface ClearRefetchQueriesAction {
  type: ActionType.ClearRefetchQueriesAction;
  payload: {
    uuid: string;
  };
}

export const clearRefetchQueries = (
  uuid: string,
): ClearRefetchQueriesAction => ({
  type: ActionType.ClearRefetchQueriesAction,
  payload: {
    uuid,
  },
});

export interface AddValidationException {
  type: ActionType.AddValidationException;
  payload: {
    exception: string;
  };
}
export const addValidationException = (
  exception: string,
): AddValidationException => ({
  type: ActionType.AddValidationException,
  payload: {
    exception,
  },
});

export interface ClearValidationException {
  type: ActionType.ClearValidationException;
  payload: {
    exception: string;
  };
}
export const clearValidationException = (
  exception: string,
): ClearValidationException => ({
  type: ActionType.ClearValidationException,
  payload: {
    exception,
  },
});

export interface FileUploadAttachments {
  attachableId: string;
  attachableType: FilesAttachable;
  files: File[];
}
export interface AddFileUploadAttachments {
  type: ActionType.AddFileUploadAttachments;
  payload: {
    uploadAttachments: FileUploadAttachments;
  };
}
export const addFileUploadAttachment = (
  uploadAttachments: FileUploadAttachments,
): AddFileUploadAttachments => ({
  type: ActionType.AddFileUploadAttachments,
  payload: {
    uploadAttachments,
  },
});

export interface ClearFileUploadAttachments {
  type: ActionType.ClearFileUploadAttachments;
  payload: {
    uploadAttachments: FileUploadAttachments;
  };
}
export const clearFileUploadAttachments = (
  uploadAttachments: FileUploadAttachments,
): ClearFileUploadAttachments => ({
  type: ActionType.ClearFileUploadAttachments,
  payload: {
    uploadAttachments,
  },
});

// Used for Managing the GlobalModal TaskCreation State.
// Note this is similar to the Standalone Task Creation page, but not 1-1.
// These fields will be set by 1 off buttons and used to Prefill the Modal State.
// IE: { isOpen: true, subjectType: "Inquiry", subjectId: "1234" }
export interface CreateTaskFormPropsInput {
  subjectId?: string;
  subjectType?: TaskContextSubjectTypeEnum;
  taskContextName?: string;
  taskType?: TypeEnum;
}
export interface CreateTaskModalStateInput extends CreateTaskFormPropsInput {
  isOpen?: boolean;
}
export interface SetCreateTaskModalStateAction {
  type: ActionType.SetCreateTaskModalState;
  payload: CreateTaskModalStateInput;
}
export const setCreateTaskModalState = (
  filter: CreateTaskModalStateInput,
): SetCreateTaskModalStateAction => ({
  type: ActionType.SetCreateTaskModalState,
  payload: filter,
});
